import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import LastViewProductsSlider from 'src/components/containers/LastViewProductsSlider/LastViewProductsSlider';
import Button from 'src/components/ui-kit/Button/Button';
import useLastViewProductEansFromCookies from 'src/hooks/useLastViewProductEansFromCookies/useLastViewProductEansFromCookies';
import { ImagesGallery } from 'src/modules/Gallery';
import { getAvailability } from 'src/redux/cart/general/helpers/getAvailability';
import useLocalization from 'src/services/localization/useLocalization';
import { getNPShippingText } from 'src/utils/delivery/getNpShippingText';
import shouldSetTintingModeToDependOnCart from '../../../HOCs/withTinting/shouldSetTintingModeToDependOnCart';
import withTinting, { WithTintingProps } from '../../../HOCs/withTinting/withTinting';
import { ProductSubscriptionsApi } from '../../../hooks/ProductSubscriptions/ProductSubscriptions.types';
import { useProductBadges } from '../../../hooks/product/useProductBadges';
import usePrevious from '../../../hooks/usePrevious/usePrevious';
import { Category, DeliveryType, Product } from '../../../redux/apiTypes';
import getCategoriesTree from '../../../utils/categories/getCategoriesTree';
import { addToCartButtonsEnum, sendProductAddedFromDetails } from '../../../utils/marketing/enhancedEcommerce';
import { getGalleryItemsFromProductImages } from '../../../utils/product/getGalleryItemsFromProductImages';
import { getImgAlt } from '../../../utils/product/getImgAttributes';
import getDataStatus from '../../../utils/system/getDataStatus';
import AddUserEmailToSubscriptionModal from '../../containers/AddUserPhoneToSubscription/AddUserEmailToSubscriptionModal';
import { BigProductCardPlace } from "../../containers/EditCartBigProductCard/EditCartBigProductCard";
import Badges from '../Badges/Badges';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import { ViewProduct } from '../ProductsCarousel/ProductsCarousel';
import { styles } from './BigProductCard.styles';
import BigProductCardDescription from './BigProductCardDescription/BigProductCardDescription';
import BigProductCardHeader from './BigProductCardHeader/BigProductCardHeader';
import BigProductCardPickerComment from './BigProductCardPickerComment/BigProductCardPickerComment';
import BigProductCardShareButton from './BigProductCardShareButton/BigProductCardShareButton';
import BigProductCardTopInfo from './BigProductCardTopInfo/BigProductCardTopInfo';
import useDiscountProps from './useDiscountProps';
import useFixedHeaderIsVisible from './useFixedHeaderIsVisible';
import { getPurchaseButtonKey } from './utils/getPurchaseButtonText';


const RecommendedProductsCarousel = dynamic(
  () => import('../RecommendedProductsCarousel/RecommendedProductsCarousel'),
  { ssr: true },
);

const SimilarProductsCarousel = dynamic(
  () => import('../SimilarProductsCarousel/SimilarProductsCarousel'),
  { ssr: true },
);


export interface BigProductCardProps {
  data: Product;
  itemIsAdded?: boolean;
  amount: number;
  isModal?: boolean;
  isFavorite?: boolean;
  canPurchase?: boolean;
  isNovaPoshtaMode?: boolean;
  addButtonTranslationKey?: string;
  categories?: Category[];
  isReplacement?: boolean;
  withoutBreadcrumbs?: boolean;
  withBasketIcon?: boolean;
  taxonsAsText?: boolean;
  itemsCanBeAdded: boolean;
  language: string;
  baseURL: string;
  place: string;
  withoutComment: boolean;
  relatedProductsAreFetching?: boolean;
  similarProducts: ViewProduct[];
  buyTogetherProducts: ViewProduct[];
  deliveryType?: DeliveryType;
  onReplace?(): void;
  onFavoriteClick(product: Product): void;
  onAddToCart(product: Product, bigCardPlace: BigProductCardPlace): void;
  onNewRouteLinkClick(): void;
  onProductTileClick(
    event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
    product: Product,
  ): void;
  onAmountChange(amount: number, place: string, product: Product): void;
  onShowMessage(translationKey: string): void;
  onReviewClick(): void;
  productSubscriptionsApi: ProductSubscriptionsApi;
  isNewFinScheme?: boolean;
  chainName: string;
  afterTopInfoSlot?: ReactNode;
}

export type Props = WithTintingProps & BigProductCardProps;

const BigProductCard = (props: Props) => {
  const {
    data,
    isModal = false,
    isFavorite,
    isTintingMode,
    isNovaPoshtaMode,
    addButtonTranslationKey,
    canPurchase = true,
    categories,
    isReplacement,
    withoutBreadcrumbs,
    withBasketIcon = true,
    taxonsAsText,
    itemIsAdded,
    amount,
    itemsCanBeAdded,
    language,
    baseURL,
    place,
    withoutComment,
    relatedProductsAreFetching,
    similarProducts,
    buyTogetherProducts,
    deliveryType,
    afterTopInfoSlot,
    setTintingMode,
    onReplace,
    onFavoriteClick,
    onAddToCart,
    onNewRouteLinkClick,
    onProductTileClick,
    onAmountChange,
    onReviewClick,
    onShowMessage,
    productSubscriptionsApi,
    isNewFinScheme,
    chainName,
  } = props;

  const {
    currency,
    discount,
    gallery,
    ean,
    price,
    price_wholesale,
    title,
    unit,
    quantity,
    weight,
    volume,
    in_stock,
    bundle,
    category_id: categoryId,
    hits,
    verbose_stock,
    shopping_list_count,
  } = data;

  const [renderIsInitialWithAutofocus, setRenderIsInitialWithAutofocus] = useState(false);
  const [tintingModeIsLocal, setTintingModeIsLocal] = useState(true);

  const isInitialMount = useRef(true);
  const previousAmount = usePrevious(amount);
  const previousItemIsAdded = usePrevious(itemIsAdded);
  const prevEan = usePrevious(ean);

  const isAvailable = getAvailability(data, deliveryType);

  const localize = useLocalization();

  const {
    subscriptions,
    addUserEmailToSubscriptionModalIsOpen,
    newUserEmail,
    newUserEmailErrorTranslationId,
    wishedSubscriptionType,
    handleClickSubscribeButton,
    closeAddUserEmailToSubscriptionModal,
    handleClickAddEmailToSubscribeButton,
    handleChangeNewUserEmail,
  } = productSubscriptionsApi;

  const newUserEmailErrorText = newUserEmailErrorTranslationId && localize(newUserEmailErrorTranslationId);

  const { addEanToCookies } = useLastViewProductEansFromCookies();
  const handleSubscribeToDiscountClick = () => handleClickSubscribeButton('discount');
  const handleSubscribeToAvailabilityClick = () => handleClickSubscribeButton('availability');

  const imgAlt = getImgAlt(title, localize, chainName);
  const galleryItems = useMemo(
    () => getGalleryItemsFromProductImages(gallery, imgAlt, title),
    [gallery, imgAlt, title],
  );

  const withHalfOpacity = !in_stock || !isAvailable;
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const fixedHeaderIsVisible = useFixedHeaderIsVisible({ scrolledElementRef: contentContainerRef, isModal });

  const { timer, discountValue, discountVariant } = useDiscountProps({ ...data, amount });

  useEffect(
    () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;

        addEanToCookies(ean);
        return;
      }

      if (prevEan !== ean) {
        addEanToCookies(ean);
        return;
      }

      const withTintingMode = shouldSetTintingModeToDependOnCart(
        itemIsAdded,
        amount,
        previousItemIsAdded,
        previousAmount,
      );

      if (ean && withTintingMode) {
        /** @todo called several times - refactor dependencies */
        setTintingMode();
      }
    },
    // eslint-disable-next-line
    [amount, ean, itemIsAdded, prevEan, previousAmount, previousItemIsAdded, setTintingMode],
  );

  const handleAddCurrentProductToCart = () => {
    setRenderIsInitialWithAutofocus(true);
    onAddToCart(data, addToCartButtonsEnum.PRODUCT_CARD);

    // to delete after promotion countdown А/B test
    sendProductAddedFromDetails({
      price,
      timer: discountValue ? timer : '',
      ean,
      isPromotion: !!discountValue,
    });
  };

  const handleAddSimilarProductToCart = (product: Product) => {
    onAddToCart(product, addToCartButtonsEnum.PRODUCT_CARD_SIMILAR);
  };

  const handleAddBuyWithProductToCart = (product: Product) => {
    onAddToCart(product, addToCartButtonsEnum.PRODUCT_CARD_BUY_WITH);
  };

  const badges = useProductBadges(data);

  const purchaseButtonKey = getPurchaseButtonKey({
    isNovaPoshtaMode,
    inStock: in_stock,
    canPurchase,
    addButtonTranslationKey,
  });
  const purchaseButtonText = localize(purchaseButtonKey);

  const categoriesBreadcrumbs = getCategoriesTree(categories, categoryId);
  const breadcrumbs = [...categoriesBreadcrumbs, { title, type: 'product' }];
  const novaPoshtaNotificationText = getNPShippingText({ deliveryType, isNewFinScheme, localize });
  const promoBudgeIsVisible = discountVariant === 1 && in_stock && !!timer;

  return (
    <div
      id={isModal ? 'BigProductCard__modal' : 'BigProductCard'}
      className={classNames(
        'BigProductCard', {
        'BigProductCard_modalMode': isModal,
        'BigProductCard_tintingMode': isTintingMode && tintingModeIsLocal,
      })}
      data-productkey={ean} // E-sputnik data-attribute
      data-status-loading={getDataStatus(relatedProductsAreFetching)}
      data-testid='big_product_card'

      data-marker-popup={isModal ? 'Big product card' : undefined}
      data-marker={!isModal ? 'Big product card' : undefined}
    >
      {
        !withoutBreadcrumbs && (
          <div
            className={classNames(
              'BigProductCard__breadcrumbsContainer', {
              'BigProductCard__breadcrumbsContainer_marginBottomExtended': isModal,
            })}
          >
            <Breadcrumbs
              items={breadcrumbs}
              noTopMargin={isModal}
              microMarkingIsShown={!isModal}
              language={language}
              baseURL={baseURL}
              onBreadcrumbClick={onNewRouteLinkClick}
            />
          </div>
        )
      }
      {!in_stock && (
        <SimilarProductsCarousel
          currentLocale={language}
          products={similarProducts}
          isFetching={relatedProductsAreFetching}
          title={localize('product_not_available')}
          withMarginTop={!in_stock && isModal}
          language={language}
          itemsCanBeAdded={itemsCanBeAdded}
          onProductTileClick={onProductTileClick}
          onAddToCart={handleAddSimilarProductToCart}
          onAmountChange={(amount, product) => onAmountChange(
            amount, addToCartButtonsEnum.PRODUCT_CARD_SIMILAR, product,
          )}
          onFavoriteClick={onFavoriteClick}
        />
      )}
      {fixedHeaderIsVisible && (
        <div className="BigProductCard__headerWrapper">
          <BigProductCardHeader
            data={data}
            amount={amount}
            unit={unit}
            quantity={quantity}
            bundle={bundle}
            place={place}
            itemIsAdded={itemIsAdded}
            initialRenderWithAutofocus={renderIsInitialWithAutofocus}
            isFavorite={isFavorite}
            addButtonTranslationKey={addButtonTranslationKey}
            onAddToCart={handleAddCurrentProductToCart}
            onAmountChange={amount => onAmountChange(amount, place, data)}
            onSetLocalTintingMode={setTintingModeIsLocal}
            onFavoriteClick={() => onFavoriteClick(data)}
            isReplacement={isReplacement}
            onReplace={onReplace}
            purchaseText={purchaseButtonText}
            purchaseIsAvailable={canPurchase}
            itemsCanBeAdded={itemsCanBeAdded}
            subscribedToAvailability={subscriptions.availability}
            onSubscribeToAvailabilityClick={handleSubscribeToAvailabilityClick}
          />
        </div>
      )}
      <div
        className='BigProductCard__content'
        data-marker='Big product card content'
        ref={contentContainerRef}
      >
        <div className="BigProductCard__section">
          <div className='BigProductCard__badges'>
            <Badges
              {...badges}
              withOpacity={withHalfOpacity}
              promo={promoBudgeIsVisible ? `${localize('promo')}: ${timer}` : ''}
              views={hits}
            />
          </div>
          <BigProductCardShareButton
            productName={title}
            onShowMessage={onShowMessage}
          />
          <ImagesGallery
            withZoom
            withHalfOpacity={withHalfOpacity}
            items={galleryItems}
          />
        </div>
        <div
          className='BigProductCard__section'
        >
          <BigProductCardTopInfo
            title={title}
            unit={unit}
            quantity={quantity}
            weight={weight}
            volume={volume}
            bundle={bundle}
            price={price}
            wholesalePrices={price_wholesale}
            currency={currency}
            discount={discount}
            isReplacement={isReplacement}
            itemIsAdded={itemIsAdded}
            amount={amount}
            place={place}
            renderIsInitialWithAutofocus={renderIsInitialWithAutofocus}
            itemIsInStock={in_stock}
            purchaseIsAvailable={canPurchase}
            withBasketIcon={withBasketIcon}
            purchaseText={purchaseButtonText}
            itemsCanBeAdded={itemsCanBeAdded}
            isFavorite={isFavorite}
            novaPoshtaIsAvailable={isNovaPoshtaMode}
            onReplace={onReplace}
            onAmountChange={amount => onAmountChange(amount, place, data)}
            onSetLocalTintingMode={setTintingModeIsLocal}
            onPurchaseClick={handleAddCurrentProductToCart}
            onFavoriteClick={() => onFavoriteClick(data)}
            subscribedToDiscount={subscriptions.discount}
            onSubscribeToDiscountClick={handleSubscribeToDiscountClick}
            subscribedToAvailability={subscriptions.availability}
            onSubscribeToAvailabilityClick={handleSubscribeToAvailabilityClick}
            novaPoshtaNotificationText={novaPoshtaNotificationText}
            stockBalance={verbose_stock}
            shoppingListCount={shopping_list_count}
          />

          {
            afterTopInfoSlot && (
              <div className="BigProductCard__afterTopInfoSlot">
                { afterTopInfoSlot }
              </div>
            )
          }

          {!withoutComment && (
            <div className="BigProductCard__comments">
              <BigProductCardPickerComment ean={ean} />
            </div>
          )}
        </div>
      </div>

      {!isReplacement &&
        <RecommendedProductsCarousel
          items={buyTogetherProducts}
          productPlace={addToCartButtonsEnum.PRODUCT_CARD_BUY_WITH}
          isFetching={relatedProductsAreFetching}
          language={language}
          itemsCanBeAdded={itemsCanBeAdded}
          onProductTileClick={onProductTileClick}
          onAddToCart={handleAddBuyWithProductToCart}
          onAmountChange={(amount, product) => onAmountChange(
            amount, addToCartButtonsEnum.PRODUCT_CARD_BUY_WITH, product,
          )}
          onFavoriteClick={onFavoriteClick}
        />
      }

      <BigProductCardDescription
        product={data}
        language={language}
        onNewRouteLinkClick={onNewRouteLinkClick}
        taxonsAsText={taxonsAsText}
      />

      <div className="BigProductCard__productFeedbackButtonBox">
        <Button
          variant='ghost'
          dataMarkerValue='Product Feedback Button'
          onClick={onReviewClick}
        >
          {localize('feedback_problems_with_the_description')}
        </Button>
      </div>

      {!isReplacement && in_stock &&
        <SimilarProductsCarousel
          currentLocale={language}
          products={similarProducts}
          isFetching={relatedProductsAreFetching}
          title={localize('product_similar_products')}
          language={language}
          itemsCanBeAdded={itemsCanBeAdded}
          onProductTileClick={onProductTileClick}
          onAddToCart={handleAddSimilarProductToCart}
          onAmountChange={(amount, product) => onAmountChange(
            amount, addToCartButtonsEnum.PRODUCT_CARD_SIMILAR, product,
          )}
          onFavoriteClick={onFavoriteClick}
        />
      }

      <LastViewProductsSlider slideCount={4} ean={ean} />

      <p className='BigProductCard__disclaimer'>
        {localize('products.gallery.disclaimer')}
      </p>

      {
        addUserEmailToSubscriptionModalIsOpen &&
        <AddUserEmailToSubscriptionModal
          onClose={closeAddUserEmailToSubscriptionModal}
          onSubscribe={handleClickAddEmailToSubscribeButton}
          email={newUserEmail}
          onChangeEmail={handleChangeNewUserEmail}
          errorText={newUserEmailErrorText}
          wishedSubscriptionType={wishedSubscriptionType}
        />
      }

      <style jsx>{styles}</style>
    </div>
  );
};

export default withTinting(BigProductCard);
